import React, { useState } from "react";
import { Badge, Button, Dialog, Flex, Text } from "@radix-ui/themes";
import { useQueryClient } from "@tanstack/react-query";

import { deactivateBraze, syncBraze } from "api/BrazeApi";
import { ActiveIntegrateCard, PermissionAlertDialog } from "components";
import { useProfileContext } from "context";
import { useGetBrazeStatus } from "hooks";
import { useToast } from "../../../context/ToastContext";

export function ActiveBrazeIntegration() {
  const { userProfile } = useProfileContext();
  const { setToastMessage } = useToast();
  const queryClient = useQueryClient();
  const { data } = useGetBrazeStatus(userProfile?.organisation_id);
  const config = data?.data || {};
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [permissionOpen, setPermissionOpen] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const isOwner = userProfile?.user_role === "Owner";

  const disconnect = async () => {
    setIsDisconnecting(true);
    try {
      await deactivateBraze(userProfile.organisation_id);
      setConfirmOpen(false);
      setToastMessage("Braze integration disconnected");
      await queryClient.invalidateQueries(["braze-status", userProfile.organisation_id]);
    } catch (error) {
      setToastMessage(error?.response?.data?.message || "Failed to disconnect Braze");
    } finally {
      setIsDisconnecting(false);
    }
  };

  const syncNow = async () => {
    setIsSyncing(true);
    try {
      await syncBraze(userProfile.organisation_id);
      setToastMessage("Braze sync queued");
    } catch (error) {
      setToastMessage(error?.response?.data?.message || "Failed to queue Braze sync");
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <>
      <ActiveIntegrateCard.Root
        name="Braze"
        subtitle="Customer engagement & CRM"
        image_url="braze.png"
        showLocationBadge={false}
        option={{
          text: "Disconnect",
          color: "red",
          onClick: () => isOwner ? setConfirmOpen(true) : setPermissionOpen(true),
        }}
      >
        <ActiveIntegrateCard.Section name="Workspace" subtitle="Connected Braze REST workspace">
          <Flex direction="column" gap="2" style={{ width: "100%" }}>
            <Text size="2" weight="medium">{config.rest_endpoint}</Text>
            <Text size="1" color="gray">The REST API key is encrypted and hidden.</Text>
          </Flex>
        </ActiveIntegrateCard.Section>

        <ActiveIntegrateCard.Section name="Data sync" subtitle="Profiles, consent and venue events">
          <Flex direction="column" gap="3" style={{ width: "100%" }}>
            <Flex align="center" gap="2">
              <Badge color="green" variant="soft">Profiles enabled</Badge>
              <Badge color={config.events_enabled ? "green" : "gray"} variant="soft">
                Venue events {config.events_enabled ? "enabled" : "disabled"}
              </Badge>
            </Flex>
            <Text size="2" color="gray">
              Locations: {(config.locations || []).map((item) => item.name).join(", ") || "None"}
            </Text>
            <Text size="1" color="gray">
              Last sync: {config.last_sync_at ? new Date(config.last_sync_at).toLocaleString() : "Not yet synced"}
            </Text>
            {config.last_sync_error ? <Text size="1" color="red">{config.last_sync_error}</Text> : null}
            <Flex justify="end">
              <Button size="2" variant="soft" onClick={syncNow} loading={isSyncing}>Sync now</Button>
            </Flex>
          </Flex>
        </ActiveIntegrateCard.Section>
      </ActiveIntegrateCard.Root>

      <Dialog.Root open={confirmOpen} onOpenChange={setConfirmOpen}>
        <Dialog.Content size="3" style={{ maxWidth: 450 }}>
          <Dialog.Title>Disconnect Braze</Dialog.Title>
          <Dialog.Description size="2" mb="4">
            Scheduled profile and event syncing will stop. Sync history is preserved for reconnection.
          </Dialog.Description>
          <Flex justify="end" gap="3">
            <Dialog.Close><Button variant="soft" color="gray">Cancel</Button></Dialog.Close>
            <Button variant="solid" color="red" onClick={disconnect} loading={isDisconnecting} disabled={isDisconnecting}>
              Disconnect
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <PermissionAlertDialog
        isOpen={permissionOpen}
        onOpenChange={setPermissionOpen}
        message="Only owners can perform this action"
      />
    </>
  );
}
